import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default:
          'bg-icon-button-primary-static-background text-icon-button-primary-static-text hover:bg-icon-button-primary-hover-background hover:text-icon-button-primary-hover-text disabled:bg-icon-button-primary-disabled-background disabled:text-icon-button-primary-disabled-text',
        secondary:
          'bg-icon-button-secondary-static-background text-icon-button-secondary-static-text hover:bg-icon-button-secondary-hover-background hover:text-icon-button-secondary-hover-text disabled:bg-icon-button-secondary-disabled-background disabled:text-icon-button-secondary-disabled-text',
        destructive:
          'bg-icon-button-destructive-static-background text-icon-button-destructive-static-text hover:bg-icon-button-destructive-hover-background hover:text-icon-button-destructive-hover-text disabled:bg-icon-button-destructive-disabled-background disabled:text-icon-button-destructive-disabled-text',
        success:
          'bg-icon-button-success-static-background text-icon-button-success-static-text hover:bg-icon-button-success-hover-background hover:text-icon-button-success-hover-text disabled:bg-icon-button-success-disabled-background disabled:text-icon-button-success-disabled-text',
        warning:
          'bg-icon-button-warning-static-background text-icon-button-warning-static-text hover:bg-icon-button-warning-hover-background hover:text-icon-button-warning-hover-text disabled:bg-icon-button-warning-disabled-background disabled:text-icon-button-warning-disabled-text',
        outline:
          'bg-icon-button-outline-static-background text-icon-button-outline-static-text border border-icon-button-outline-static-border hover:bg-icon-button-outline-hover-background hover:text-icon-button-outline-hover-text',
        ghost:
          'bg-icon-button-ghost-static-background text-icon-button-ghost-static-text hover:bg-icon-button-ghost-hover-background hover:text-icon-button-ghost-hover-text disabled:bg-icon-button-ghost-disabled-background disabled:text-icon-button-ghost-disabled-text',
        link: 'bg-icon-button-link-static-background text-icon-button-link-static-text hover:bg-icon-button-link-hover-background hover:text-icon-button-link-hover-text disabled:bg-icon-button-link-disabled-background disabled:text-icon-button-link-disabled-text',
      },
      size: {
        sm: 'h-9 w-9 rounded-md px-3 text-xs',
        lg: 'h-11 w-11 rounded-md px-8 text-sm',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'icon',
    },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const IconButton = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
});
IconButton.displayName = 'Button';

export { IconButton, buttonVariants };
